import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { quizAPI } from '../services/api';
import { FiArrowLeft, FiZap } from 'react-icons/fi';

const QuizSelection = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [trades, setTrades] = useState([]);
  const [formData, setFormData] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    numQuestions: 10,
    difficulty: 'medium',
    useAI: false,
    topic: '',
    language: 'english',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await quizAPI.getCategories();
      if (response.data.success) {
        setCategories(response.data.tradegroups);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleTradegroupChange = async (e) => {
    const groupId = e.target.value;
    setFormData({ ...formData, tradegroup: groupId, trade: '' });
    
    if (groupId) {
      try {
        const response = await quizAPI.getTrades(groupId);
        setTrades(response.data);
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    }
  };

  const handleStartQuiz = async () => {
    setLoading(true);
    try {
      let questions;
      
      if (formData.useAI) {
        // Generate with AI
        const response = await quizAPI.generateQuiz({
          topic: formData.topic || 'General Knowledge',
          num_questions: formData.numQuestions,
          difficulty: formData.difficulty,
          language: formData.language,
        });
        questions = response.data.questions;
      } else {
        // Get from database
        if (!formData.tradegroup) {
          alert('Please select a category');
          setLoading(false);
          return;
        }
        const response = await quizAPI.getQuestions({
          tradegroup: formData.tradegroup,
          trade: formData.trade,
          subject: formData.subject,
          chapter: formData.chapter,
          limit: formData.numQuestions,
        });
        questions = response.data.questions;
      }

      if (!questions || questions.length === 0) {
        alert('No questions available. Please try different settings.');
        setLoading(false);
        return;
      }

      navigate('/quiz', { state: { questions, config: formData } });
    } catch (error) {
      console.error('Error starting quiz:', error);
      alert('Failed to load quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Link to="/dashboard" className="mr-4 p-2 rounded-lg hover:bg-gray-100">
              <FiArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">Select Quiz Settings</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card">
          <div className="space-y-6">
            {/* AI Toggle */}
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-lg">
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center space-x-3">
                  <FiZap className="w-6 h-6 text-primary-500" />
                  <div>
                    <p className="font-semibold text-gray-800">Generate with AI</p>
                    <p className="text-sm text-gray-600">
                      Use AI to generate custom questions on any topic
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.useAI}
                  onChange={(e) => setFormData({ ...formData, useAI: e.target.checked })}
                  className="w-6 h-6 text-primary-500 rounded focus:ring-primary-500"
                />
              </label>
            </div>

            {formData.useAI ? (
              /* AI Mode */
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Topic *
                  </label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="input-field"
                    placeholder="e.g., React JS, Indian History, Mathematics"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Language
                  </label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="input-field"
                  >
                    <option value="english">English</option>
                    <option value="hindi">Hindi (हिंदी)</option>
                    <option value="hinglish">Hinglish</option>
                  </select>
                </div>
              </>
            ) : (
              /* Database Mode */
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category (Trade Group) *
                  </label>
                  <select
                    value={formData.tradegroup}
                    onChange={handleTradegroupChange}
                    className="input-field"
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {trades.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Trade
                    </label>
                    <select
                      value={formData.trade}
                      onChange={(e) => setFormData({ ...formData, trade: e.target.value })}
                      className="input-field"
                    >
                      <option value="">Select Trade</option>
                      {trades.map((trade) => (
                        <option key={trade.id} value={trade.id}>
                          {trade.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Common Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Questions
                </label>
                <input
                  type="number"
                  value={formData.numQuestions}
                  onChange={(e) => setFormData({ ...formData, numQuestions: parseInt(e.target.value) })}
                  className="input-field"
                  min="5"
                  max="50"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="input-field"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3">Quiz Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Questions</p>
                  <p className="font-semibold text-gray-800">{formData.numQuestions}</p>
                </div>
                <div>
                  <p className="text-gray-600">Difficulty</p>
                  <p className="font-semibold text-gray-800 capitalize">{formData.difficulty}</p>
                </div>
                <div>
                  <p className="text-gray-600">Time (approx)</p>
                  <p className="font-semibold text-gray-800">{formData.numQuestions} min</p>
                </div>
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={handleStartQuiz}
              disabled={loading || (!formData.useAI && !formData.tradegroup)}
              className="btn-primary w-full text-lg py-4 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading Quiz...
                </span>
              ) : (
                'Start Quiz'
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuizSelection;

